import React, { useState, useEffect } from "react";
import {
  Search,
  Save,
  Plus,
  ChevronRight,
  Edit,
  Loader2,
  AlertCircle,
  Image as ImageIcon,
} from "lucide-react";
import Modal from "../common/CommonModal";

const ChequeRegister = () => {
  const COMPANY_CODE = localStorage?.getItem("companyCode") || "-";
  let API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  const BASE_URL = API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";

  const [banks, setBanks] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [modalMode, setModalMode] = useState("ENTRY");

  const [formData, setFormData] = useState({
    BankName: "",
    account: "",
    date: new Date().toISOString().split("T")[0],
    chqDigits: "",
    seriesStart: "",
    seriesEnd: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const fetchBanks = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/CHQBANKLIST/-/-`,
      );
      const data = await response.json();
      setBanks(data);
      if (data.length > 0 && !selectedBank) setSelectedBank(data[0]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanks();
  }, []);

  useEffect(() => {
    if (selectedBank?.account) {
      const fetchLedger = async () => {
        setLedgerLoading(true);
        try {
          const response = await fetch(
            `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/CHQREGLIST/${selectedBank.account}/-`,
          );
          setLedger(await response.json());
        } catch (error) {
          console.error(error);
        } finally {
          setLedgerLoading(false);
        }
      };
      fetchLedger();
    }
  }, [selectedBank]);

  const openAddModal = () => {
    setModalMode("ENTRY");
    setFormData({
      BankName: "",
      account: "",
      date: new Date().toISOString().split("T")[0],
      chqDigits: "6",
      seriesStart: "",
      seriesEnd: "",
    });
    setShowModal(true);
  };

  const openEditModal = (e, bank) => {
    e.stopPropagation();
    setModalMode("EDIT");
    // Auto-fill: Split the existing "0001-0100" string back into Start and End inputs
    const seriesParts = bank.LastCheque?.split("-") || ["", ""];
    setFormData({
      BankName: bank.BankName,
      account: bank.account,
      date: bank.date?.split("T")[0] || new Date().toISOString().split("T")[0],
      chqDigits: bank.chqDigits || "6",
      seriesStart: seriesParts[0] || "",
      seriesEnd: seriesParts[1] || "",
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    // Validation: Ensure range is provided
    if (!formData.seriesStart || !formData.seriesEnd) {
      alert("Please provide both Series Start and Series End numbers.");
      return;
    }

    setIsSaving(true);
    // Formatting the LastCheque as "START-END"
    const lastChequeString = `${formData.seriesStart}-${formData.seriesEnd}`;

    const payload = [
      {
        CmpCode: COMPANY_CODE,
        mode: modalMode,
        account: formData.account,
        BankName: formData.BankName,
        date: formData.date,
        chqDigits: formData.chqDigits,
        LastCheque: lastChequeString, // This creates the series in the backend
      },
    ];

    try {
      const res = await fetch(`${BASE_URL}/api/CRMChequeRegister`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setShowModal(false);
        fetchBanks(); // Refresh list to show new bank/updated series
      }
    } catch (error) {
      console.error("Save Error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Helper to calculate total count for UI preview
  const getChequeCount = () => {
    const start = parseInt(formData.seriesStart);
    const end = parseInt(formData.seriesEnd);
    return !isNaN(start) && !isNaN(end) ? Math.max(0, end - start + 1) : 0;
  };

  return (
    <div className="flex flex-col px-6 py-4 bg-slate-50/50 text-slate-700 min-h-screen">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-slate-800">Cheque Management</h1>
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl font-bold text-sm hover:bg-blue-700 shadow-md"
        >
          <Plus size={16} /> Add New Bank
        </button>
      </div>

      <div className="grid grid-cols-12 gap-6 overflow-hidden">
        {/* Ledger Table Section */}
        <div className="col-span-8 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="p-4 border-b flex justify-between items-center bg-white">
            <h3 className="font-bold text-slate-800">
              Ledger: {selectedBank?.BankName || "Select a Bank"}
            </h3>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Filter cheques..."
                className="pl-10 pr-4 py-2 bg-slate-50 border rounded-lg text-xs outline-none w-64 focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="overflow-auto h-[600px] relative">
            {ledgerLoading && (
              <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-20">
                <Loader2 className="animate-spin text-blue-500" />
              </div>
            )}
            <table className="w-full text-xs text-left">
              <thead className="sticky top-0 bg-slate-50 text-slate-500 uppercase font-bold z-10">
                <tr>
                  <th className="p-4">Cheque No</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Issued To</th>
                  <th className="p-4 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {ledger.map((item, i) => (
                  <tr key={i} className="hover:bg-slate-50 transition-colors">
                    <td className="p-4 font-mono font-bold text-blue-600">
                      {item.chequeNo}
                    </td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-1 rounded-md text-[10px] font-bold border ${item.Status === "BLANK" ? "bg-blue-50 text-blue-600 border-blue-100" : "bg-green-50 text-green-600 border-green-100"}`}
                      >
                        {item.Status}
                      </span>
                    </td>
                    <td className="p-4 font-medium">{item.issuedto || "—"}</td>
                    <td className="p-4 text-right font-bold text-slate-900">
                      {parseFloat(item.amount || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Sidebar Section */}
        <div className="col-span-4 space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-2 max-h-[400px] overflow-y-auto shadow-sm">
            <p className="text-[10px] font-bold text-slate-400 uppercase p-2 tracking-widest">
              Active Accounts
            </p>
            {banks.map((bank, i) => (
              <div
                key={i}
                onClick={() => setSelectedBank(bank)}
                className={`group flex items-center justify-between p-4 rounded-xl cursor-pointer mb-1 transition-all ${selectedBank?.account === bank.account ? "bg-blue-600 text-white shadow-lg" : "hover:bg-slate-50 border border-transparent"}`}
              >
                <div>
                  <p
                    className={`text-[10px] font-bold uppercase ${selectedBank?.account === bank.account ? "text-blue-100" : "text-slate-400"}`}
                  >
                    {bank.BankName}
                  </p>
                  <p className="font-bold">{bank.account}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => openEditModal(e, bank)}
                    className={`p-2 rounded-lg transition-colors ${selectedBank?.account === bank.account ? "hover:bg-white/20" : "hover:bg-slate-200 text-slate-400"}`}
                  >
                    <Edit size={14} />
                  </button>
                  <ChevronRight
                    size={16}
                    className={
                      selectedBank?.account === bank.account
                        ? "text-white"
                        : "text-slate-300"
                    }
                  />
                </div>
              </div>
            ))}
          </div>

          <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 space-y-4">
            <h4 className="text-[12px] font-semibold text-slate-400">
              Preview & Master
            </h4>
            <div className="h-24 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400">
              <ImageIcon className="w-6 h-6 mb-1 opacity-50" />
              <span className="text-[10px] font-bold">No Image Attached</span>
            </div>
            <button className="flex items-center justify-center gap-2 w-full bg-slate-800 text-white py-3 rounded-xl font-bold text-sm hover:bg-slate-900 transition-all shadow-md">
              <Save className="w-4 h-4" /> Save Master Record
            </button>
          </section>
        </div>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={modalMode === "ENTRY" ? "New Bank Account" : "Edit Bank Account"}
        size="xl"
      >
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-500">
                Bank Name
              </label>
              <input
                name="BankName"
                value={formData.BankName}
                onChange={handleInputChange}
                type="text"
                className="w-full border rounded-xl h-10 px-4 text-sm focus:ring-2 focus:ring-blue-500/20 outline-none"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-500">
                Account Number
              </label>
              <input
                name="account"
                value={formData.account}
                onChange={handleInputChange}
                type="text"
                className="w-full border rounded-xl h-10 px-4 text-sm focus:ring-2 focus:ring-blue-500/20 outline-none"
                disabled={modalMode === "EDIT"}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-500">Date</label>
              <input
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                type="date"
                className="w-full border rounded-xl h-10 px-3 text-sm outline-none"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-500">
                Cheque Digits
              </label>
              <input
                name="chqDigits"
                value={formData.chqDigits}
                onChange={handleInputChange}
                type="number"
                className="w-full border rounded-xl h-10 px-4 text-sm outline-none"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-500">
                Process Mode
              </label>
              <div className="h-10 flex items-center px-4 bg-slate-50 border rounded-xl text-xs font-bold text-blue-600 uppercase">
                {modalMode}
              </div>
            </div>
          </div>

          <div className="bg-slate-900 p-5 rounded-2xl border border-slate-700 shadow-inner">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-2 text-blue-400">
                <AlertCircle size={14} />
                <span className="text-[10px] font-bold uppercase tracking-wider">
                  Series Configuration
                </span>
              </div>
              <span className="text-[10px] bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded border border-blue-500/30 font-bold uppercase">
                {getChequeCount()} Cheques to generate
              </span>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase">
                  Series Start
                </label>
                <input
                  name="seriesStart"
                  value={formData.seriesStart}
                  onChange={handleInputChange}
                  placeholder="e.g. 000001"
                  type="text"
                  className="w-full bg-slate-800 border-slate-700 text-white rounded-lg h-10 px-4 text-sm outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold text-slate-400 uppercase">
                  Series End
                </label>
                <input
                  name="seriesEnd"
                  value={formData.seriesEnd}
                  onChange={handleInputChange}
                  placeholder="e.g. 000100"
                  type="text"
                  className="w-full bg-slate-800 border-slate-700 text-white rounded-lg h-10 px-4 text-sm outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              onClick={() => setShowModal(false)}
              className="px-6 py-2 font-bold text-slate-500 hover:bg-slate-100 rounded-xl transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-blue-600 text-white px-10 py-2 rounded-xl font-bold flex items-center gap-2 hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all disabled:opacity-50"
            >
              {isSaving ? (
                <Loader2 className="animate-spin w-4 h-4" />
              ) : (
                <Save size={16} />
              )}
              {modalMode === "ENTRY"
                ? "Create Account & Series"
                : "Update Series"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ChequeRegister;
